import React from 'react';

import type { ExtractInjectionAPI, ToolbarUIComponentFactory } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { AtlassianIntelligenceToolbarButton } from '../components/AtlassianIntelligenceToolbarButton/AtlassianIntelligenceToolbarButton';
import { useIsAiEntryPointDisabled } from './utils/use-is-ai-entry-point-disabled';

const PrimaryToolbarLegacyComponent = ({
	api,
	editorView,
	editorPluginAIProvider,
	aiGlobalOptIn,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView;
}) => {
	const aiButtonState = useSharedPluginStateSelector(api, 'aiExperience.aiButtonState');
	const isAiEntryPointDisabled = useIsAiEntryPointDisabled(api);
	const onClick = () => {
		const { state, dispatch } = editorView;
		const configItem = editorPluginAIProvider.baseGenerate;
		const openAIModalCommand = createOpenAIModalCommand({
			state,
			configItem,
			lastTriggeredFrom: 'mainToolbar',
			aiGlobalOptIn,
			triggeredFor: aiButtonState?.triggeredFor,
		});
		openAIModalCommand(state, dispatch);
	};
	return (
		<AtlassianIntelligenceToolbarButton
			ButtonWrapper={editorPluginAIProvider?.AIButtonWrapper}
			onClick={onClick}
			disabled={isAiEntryPointDisabled || !aiButtonState?.isSelectionValidForAIExperience}
			view={editorView}
		/>
	);
};

export const getPrimaryToolbarLegacyComponent =
	({
		api,
		editorPluginAIProvider,
		aiGlobalOptIn,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		api: ExtractInjectionAPI<AIPlugin> | undefined;
		editorPluginAIProvider: EditorPluginAIProvider;
	}): ToolbarUIComponentFactory =>
	({ editorView }) => {
		if (!editorView) {
			return null;
		}

		return (
			<PrimaryToolbarLegacyComponent
				api={api}
				editorView={editorView}
				editorPluginAIProvider={editorPluginAIProvider}
				aiGlobalOptIn={aiGlobalOptIn}
			/>
		);
	};
